import TuneIcon from '@mui/icons-material/Tune';
import { Theme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { BooleanField, BooleanInput, Create, Datagrid, Edit, EditButton, FormTab, List, NumberField, NumberInput, ReferenceManyField, SelectInput, Show, ShowActions, SimpleList, SimpleShowLayout, TabbedForm, TextField, TextInput, required, usePermissions, useResourceDefinition } from 'react-admin';
import { useParams } from 'react-router-dom';
import AddOne2ManyButton from 'utils/react-admin/AddOne2ManyButton';
import DateFnsField from 'utils/react-admin/DateFnsField';
import GridWrapper from 'utils/react-admin/GridWrapper';
import DateRangeFilterInput from 'utils/react-admin/filters/DateRangeFilterInput';
import { Permissions } from '../../ra-php-crud-api-auth';

export const CHOICES_OPTION_OPTION_TYPE = [
  { id: 'sharing_others_category', name: 'sharing_others_category', },
  { id: 'highlight_category', name: 'highlight_category', },
  { id: 'highlight_sub_category', name: 'highlight_sub_category', },
  { id: 'reading_category', name: 'reading_category', },
  { id: 'reading_sub_category', name: 'reading_sub_category', },
  { id: 'audio_category', name: 'audio_category', },
  { id: 'audio_sub_category', name: 'audio_sub_category', },
  { id: 'channel_category', name: 'channel_category', },
  { id: 'channel_sub_category', name: 'channel_sub_category', },
  { id: 'activity_category', name: 'activity_category', },
  { id: 'question_category', name: 'question_category', },
];
// const RESOURCE_FIELDS = {}
const ResourceFilters = [
  <TextInput source="search" alwaysOn />,
  <BooleanInput source="is_public" fullWidth />,
  <NumberInput source="tree_node_parent_id" fullWidth />,
  <NumberInput source="tree_node_parent_order" fullWidth />,
  <SelectInput source="option_type" fullWidth choices={CHOICES_OPTION_OPTION_TYPE} />,
  <TextInput source="name_en" fullWidth />,
  <TextInput source="name_tc" fullWidth />,
  <TextInput source="name_sc" fullWidth />,
  <TextInput source="desc_en" fullWidth />,
  <TextInput source="desc_tc" fullWidth />,
  <TextInput source="desc_sc" fullWidth />,
  <DateRangeFilterInput source="created" fullWidth />,
  <TextInput source="createdBy" fullWidth />,
  <DateRangeFilterInput source="updated" fullWidth />,
  <TextInput source="updatedBy" fullWidth />,
];

const validateIsPublic: any[] = [required()];
const validateTreeNodeParentId: any[] = [];
const validateTreeNodeParentOrder: any[] = [];
const validateOptionType: any[] = [required()];
const validateNameEn: any[] = [required()];
const validateNameTc: any[] = [required()];
const validateNameSc: any[] = [required()];
const validateDescEn: any[] = [];
const validateDescTc: any[] = [];
const validateDescSc: any[] = [];

export const ResourceList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <List filters={ResourceFilters} sort={{ field: 'created', order: 'DESC' }}>
      {isSmall ? (
        <SimpleList linkType="edit"
          primaryText={(record: any) => `${record.name_tc} (#${record.id})`}
        ></SimpleList>)
        : (
          <Datagrid>
            <TextField source="id" />
            <BooleanField source="is_public" fullWidth />
            <NumberField source="tree_node_parent_id" fullWidth />
            <NumberField source="tree_node_parent_order" fullWidth />
            <TextField source="option_type" fullWidth />
            <TextField source="name_en" fullWidth />
            <TextField source="name_tc" fullWidth />
            <TextField source="name_sc" fullWidth />
            <DateFnsField source="created" />
            <DateFnsField source="updated" />
            {/* <ShowButton /> */}
            <EditButton />
          </Datagrid>)
      }
    </List>
  )
};

export const ResourceShow = () => {
  const { isLoading, permissions } = usePermissions<Permissions>();
  const { hasEdit } = useResourceDefinition();

  if (isLoading || undefined === permissions) { return (<>Loading</>); }

  return (
    <Show actions={hasEdit ? <ShowActions /> : <></>}>
      <SimpleShowLayout>
        <TextField source="id" />
        <BooleanField source="is_public" fullWidth />
        <NumberField source="tree_node_parent_id" fullWidth />
        <NumberField source="tree_node_parent_order" fullWidth />
        <TextField source="option_type" fullWidth />
        <TextField source="name_en" fullWidth />
        <TextField source="name_tc" fullWidth />
        <TextField source="name_sc" fullWidth />
        <TextField source="desc_en" fullWidth />
        <TextField source="desc_tc" fullWidth />
        <TextField source="desc_sc" fullWidth />
        <DateFnsField source="created" />
        <DateFnsField source="updated" />
      </SimpleShowLayout>
    </Show>
  )
};

const transform = (data: any) => {
  console.log('transform', data);
  return data;
};

export const ResourceForm = ({ isEdit }: { isEdit?: boolean }) => {
  // const { getMany } = useDataProvider();
  const { ref, ref_id, ref_default_values } = useParams();
  // const redirect = ref_id ? `/${ref}/${ref_id}` : undefined;
  const defaultValueRefId = ref_id ? { [`${ref}_id`]: ref_id } : {};
  const refDefaultValues = ref_default_values ? JSON.parse(ref_default_values as string) : {};

  return (
    <TabbedForm
      defaultValues={{ ...defaultValueRefId, ...refDefaultValues }}
    >
      {/* redirect={redirect} */}
      <FormTab label="ra.page.general">
        <GridWrapper>
          <Grid item xs={6}></Grid>
        </GridWrapper>
        <BooleanInput source="is_public" fullWidth validate={validateIsPublic} />
        <NumberInput source="tree_node_parent_id" fullWidth validate={validateTreeNodeParentId} />
        <NumberInput source="tree_node_parent_order" fullWidth validate={validateTreeNodeParentOrder} />
        <SelectInput source="option_type" fullWidth choices={CHOICES_OPTION_OPTION_TYPE} validate={validateOptionType} />
        <TextInput source="name_en" fullWidth validate={validateNameEn} />
        <TextInput source="name_tc" fullWidth validate={validateNameTc} />
        <TextInput source="name_sc" fullWidth validate={validateNameSc} />
        <TextInput source="desc_en" fullWidth validate={validateDescEn} />
        <TextInput source="desc_tc" fullWidth validate={validateDescTc} />
        <TextInput source="desc_sc" fullWidth validate={validateDescSc} />
      </FormTab>
      <FormTab label="Activities">
        <AddOne2ManyButton basepath="activities" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="activities"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Audio">
        <AddOne2ManyButton basepath="audio" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="audio"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Channel">
        <AddOne2ManyButton basepath="channel" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="channel"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Highlight">
        <AddOne2ManyButton basepath="highlight" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="highlight"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Opinion">
        <AddOne2ManyButton basepath="opinion" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="opinion"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Questions">
        <AddOne2ManyButton basepath="questions" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="questions"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Reading">
        <AddOne2ManyButton basepath="reading" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="reading"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      <FormTab label="Sharing Others">
        <AddOne2ManyButton basepath="sharing_others" label="Add" defaultValues={{}} />
        <ReferenceManyField
          reference="sharing_others"
          target="option_id"
          fullWidth
        >
          <Datagrid>
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
    </TabbedForm>
  )
}

export const ResourceCreate = () => {
  return (
    <Create mutationMode="pessimistic" transform={transform}>
      <ResourceForm />
    </Create>
  )
};

export const ResourceEdit = () => {
  return (
    <Edit mutationMode="pessimistic" transform={transform}>
      <ResourceForm isEdit={true} />
    </Edit>
  )
};

const Resource = {
  list: ResourceList,
  edit: ResourceEdit,
  create: ResourceCreate,
  // show: ResourceShow,
  icon: TuneIcon,
}

export default Resource;
